// Busy buttons keep focus (v1.3 ticket 22, owner ruling Q35).
//
// PrimeReact 10.6.2's Button `loading` draws a spinner and *disables* the button while its request runs. A focused button
// that becomes disabled loses focus: after a real Return on Add (Add New Holding, Raise Service Request, Add new comment)
// or on a download button, Chrome sent focus to <body> until the request settled — the next Tab started again from the
// top of the page (or, in a dialog, from its close button) — and a screen reader heard nothing about the wait.
//
// Here the button keeps v1.2's busy look (the spinner and the dimmed `p-disabled` style, which also stops the mouse) but
// stays enabled, so focus never leaves it; it says it is busy (`aria-disabled`, `aria-busy`), and presses are ignored
// until the request settles (the keyboard still reaches a button the mouse cannot). PrimeReact sets the native
// `disabled` from `loading` itself; its pass-through options are merged over that, so `pt.root.disabled: false` undoes
// it without forking the library (pinned by __tests__/Q35-busy-buttons.test.js, so an upgrade cannot undo it silently).
// Only while busy (review of 22): pass-through options win over props, so sent always they would also undo a caller's
// own `disabled`.
//
// Usage — the caller keeps its own busy state, and passes its press handler through:
//   <Button label="Add" raised {...busyButtonProps(isAdding, addNewThing)} />
/**
 * @param {boolean} busy  true while the button's request is on its way
 * @param {(event: React.MouseEvent) => void} onPress  what a press does when the button is not busy
 * @returns props for a PrimeReact Button: `loading`, the busy states, `onClick` and `pt`
 */
export function busyButtonProps(busy, onPress) {
  return {
    loading: busy,
    "aria-disabled": busy ? "true" : undefined,
    "aria-busy": busy ? "true" : undefined,
    onClick: (event) => {
      if (busy) return; // a second Return, Space or click while the first request runs sends nothing
      onPress(event);
    },
    pt: busy ? { root: { disabled: false } } : undefined,
  };
}
